import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Button,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReplayIcon from '@mui/icons-material/Replay';
import { CeleryTask } from '../api/celery';
import { ColorsEnum } from '../common/theme/colors';

// ── Helpers ────────────────────────────────────────────────────────
const STATE_COLOURS: Record<string, string> = {
  PENDING: ColorsEnum.sunshine,
  RECEIVED: ColorsEnum.machoBlue,
  STARTED: ColorsEnum.lighterMachoBlue,
  SUCCESS: ColorsEnum.green,
  FAILURE: ColorsEnum.error,
  RETRY: ColorsEnum.lavender,
  REVOKED: ColorsEnum.grey,
};

const fmtTs = (ts?: number): string => {
  if (!ts) return '—';
  return new Date(ts * 1000).toLocaleString();
};

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text).catch(() => {});
};

// ── Types ──────────────────────────────────────────────────────────
interface TaskDetailProps {
  task: CeleryTask;
  onClose: () => void;
  onCancelTask: (taskId: string) => Promise<{ ok: boolean; message: string; statusCode?: number }>;
  cancelLoading?: boolean;
  onBack?: () => void;
  onRerunTask?: (taskId: string) => Promise<{ ok: boolean; newTaskId?: string | null; message: string }>;
  rerunLoading?: boolean;
}

// ── Row Helper ─────────────────────────────────────────────────────
const Row: React.FC<{ label: string; value?: React.ReactNode; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => (
  <Box sx={{ display: 'flex', py: 0.75, gap: 1 }}>
    <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 110, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        wordBreak: 'break-all',
        fontFamily: mono ? 'monospace' : 'inherit',
        fontSize: mono ? '0.8rem' : undefined,
      }}
    >
      {value ?? '—'}
    </Typography>
  </Box>
);

// ── Component ──────────────────────────────────────────────────────
const TaskDetail: React.FC<TaskDetailProps> = ({ task, onClose, onCancelTask, cancelLoading = false, onBack, onRerunTask, rerunLoading = false }) => {
  const state = (task.state || 'UNKNOWN').toUpperCase();
  const cancellableStates = new Set(['PENDING', 'RECEIVED', 'STARTED', 'RETRY']);
  const terminalStates = new Set(['SUCCESS', 'FAILURE', 'REVOKED']);
  const canCancel = !!task.id && cancellableStates.has(state);
  const canRerun = !!task.id && !!task.name && terminalStates.has(state) && !!onRerunTask;
  const isLiveRunning = state === 'STARTED';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Task Detail
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onBack && (
            <Button
              size="small"
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
            >
              Back
            </Button>
          )}
          {canRerun && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={rerunLoading ? <CircularProgress size={14} /> : <ReplayIcon />}
              disabled={rerunLoading}
              onClick={() => onRerunTask!(task.id)}
            >
              {rerunLoading ? 'Rerunning…' : 'Rerun Task'}
            </Button>
          )}
          {canCancel && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<StopCircleOutlinedIcon />}
              disabled={cancelLoading}
              onClick={() => onCancelTask(task.id)}
            >
              {cancelLoading ? 'Cancelling…' : 'Cancel Task'}
            </Button>
          )}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {isLiveRunning && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Live updates every 10 seconds while task is running.
            </Typography>
          </Box>
        )}

        {task.live_status && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="overline" color="text.secondary">
                Live Worker Snapshot
              </Typography>
              <Row label="Worker" value={task.live_status.worker || task.worker || '—'} />
              <Row label="Queue State" value={(task.live_status.bucket || '—').toUpperCase()} />
              {task.live_status.snapshot && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Snapshot:
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      fontSize: '0.75rem',
                      whiteSpace: 'pre-wrap',
                      bgcolor: 'action.hover',
                      p: '8px 12px',
                      borderRadius: 1,
                      mt: 0.5,
                      maxHeight: 280,
                      overflow: 'auto',
                    }}
                  >
                    {JSON.stringify(task.live_status.snapshot, null, 2)}
                  </Box>
                </Box>
              )}
            </Box>
            <Divider sx={{ my: 1 }} />
          </>
        )}

        {/* Identity */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Identity
          </Typography>
          <Row
            label="Task ID"
            value={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {task.id}
                </span>
                <IconButton size="small" onClick={() => copyToClipboard(task.id)}>
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            }
          />
          <Row label="Name" value={task.name || '(unnamed)'} />
          <Row
            label="State"
            value={
              <Chip
                label={state}
                size="small"
                sx={{
                  bgcolor: STATE_COLOURS[state] || ColorsEnum.grey,
                  color: ColorsEnum.white,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  borderRadius: '12px',
                }}
              />
            }
          />
          <Row label="Worker" value={task.worker || '—'} />
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Timestamps */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Timestamps
          </Typography>
          <Row label="Received" value={fmtTs(task.received)} />
          <Row label="Started" value={fmtTs(task.started)} />
          <Row label="Succeeded" value={fmtTs(task.succeeded)} />
          <Row label="Failed" value={fmtTs(task.failed)} />
          <Row label="Retried" value={fmtTs(task.retried)} />
          <Row
            label="Runtime"
            value={task.runtime != null ? `${task.runtime.toFixed(3)}s` : '—'}
          />
          <Row label="ETA" value={task.eta || '—'} />
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Arguments */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Arguments
          </Typography>
          <Row label="args" value={task.args || '—'} mono />
          <Row label="kwargs" value={task.kwargs || '—'} mono />
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Routing */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Routing
          </Typography>
          <Row label="Exchange" value={task.exchange || '—'} />
          <Row label="Routing Key" value={task.routing_key || '—'} />
        </Box>

        {/* Result / Error */}
        {(task.result || task.exception || task.traceback) && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box>
              <Typography variant="overline" color="text.secondary">
                Result / Error
              </Typography>
              {task.result && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Result:
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      fontSize: '0.8rem',
                      whiteSpace: 'pre-wrap',
                      bgcolor: 'action.hover',
                      p: '8px 12px',
                      borderRadius: 1,
                      mt: 0.5,
                    }}
                  >
                    {typeof task.result === 'string'
                      ? task.result
                      : JSON.stringify(task.result, null, 2)}
                  </Box>
                </Box>
              )}
              {task.exception && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="error">
                    Exception:
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      fontSize: '0.8rem',
                      color: 'error.main',
                      whiteSpace: 'pre-wrap',
                      bgcolor: 'error.light',
                      p: '8px 12px',
                      borderRadius: 1,
                      mt: 0.5,
                    }}
                  >
                    {task.exception}
                  </Box>
                </Box>
              )}
              {task.traceback && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="error">
                    Traceback:
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      fontSize: '0.75rem',
                      color: 'error.main',
                      whiteSpace: 'pre-wrap',
                      bgcolor: 'error.light',
                      p: '8px 12px',
                      borderRadius: 1,
                      mt: 0.5,
                      maxHeight: 300,
                      overflow: 'auto',
                    }}
                  >
                    {task.traceback}
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default TaskDetail;
